import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { Box, Flex, Text, Stack, SimpleGrid, Spinner, Link } from "@chakra-ui/react";
import { CalendarDays, ClipboardCheck, Siren, CheckCircle2, Users, ClipboardList, BarChart3, BookOpen } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { cachePut, cacheGet, safeQuery, safeAll } from "@/lib/offline";
import { isUrgent, urgentOrFilter } from "@/lib/urgent";
import { SafetyBulletinsCard } from "@/components/SafetyBulletinsCard";
import { AssignedTasks } from "@/pages/Tasks";
import { NeedsAttention } from "@/components/NeedsAttention";
import { Greeting, HomeCard, QuickLink, StatTile, EmptyState, RecentSubs } from "@/pages/home/shared";
import { ScheduleFormButton } from "@/components/ScheduleFormButton";
import { colors } from "@/theme/colors";

const ACCENT_AMBER = { bg: "#FBEEDA", fg: "#8A5106" };
const ACCENT_POPPY = { bg: colors.poppyTint, fg: colors.poppy };
const ACCENT_TEAL = { bg: colors.tealTint, fg: colors.teal };
const ACCENT_SAGE = { bg: "#EAF0E7", fg: colors.sageDeep };
const ACCENT_RESOLVED = { bg: colors.resolvedBg, fg: colors.resolvedText };

export default function AdminHome() {
  const { user, profile, companyName, hasFeature, activeCompanyId } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState([]);
  const [unknown, setUnknown] = useState(false);
  const [urgentCount, setUrgentCount] = useState(null);

  const load = useCallback(async () => {
    const [{ data, error }, cntRes] = await safeAll([
      supabase.from("submissions")
        .select("id,template_title,template_type,worker_name,location_name,status,flagged,revision,created_at")
        .eq("company_id", activeCompanyId)
        .order("created_at", { ascending: false }).limit(300),
      // Server-side count for the "Needs review" tile — the 300-row fetch above
      // truncates on large tenants and would under-count (matches Urgent Items query).
      supabase.from("submissions").select("id", { count: "exact", head: true })
        .eq("company_id", activeCompanyId).eq("status", "pending").or(urgentOrFilter()),
    ]);
    const cnt = cntRes.error ? null : cntRes.count;
    if (error) {
      // Load failed: cached data (even a real zero) is real data; unknown only when nothing was ever loaded.
      const cached = cacheGet(user.id, "home_subs");
      setSubs(cached ? cached.subs || [] : []);
      setUrgentCount(cached ? cached.urgentCount ?? null : null);
      setUnknown(!cached);
    } else {
      setSubs(data || []);
      setUrgentCount(cnt);
      setUnknown(false);
      cachePut(user.id, "home_subs", { subs: data || [], urgentCount: cnt });
    }
    setLoading(false);
  }, [user.id, activeCompanyId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Flex minH="40vh" align="center" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>;

  const active = subs.filter((s) => s.status !== "superseded");
  const weekStart = new Date(); weekStart.setDate(weekStart.getDate() - 6); weekStart.setHours(0, 0, 0, 0);
  const week = active.filter((s) => new Date(s.created_at) >= weekStart);
  const pending = active.filter((s) => s.status === "pending");
  const resolved = active.filter((s) => s.status === "resolved");
  const openIncidents = pending.filter((s) => s.template_type === "Incident Report");
  const recent = active.slice(0, 6);

  return (
    <Box data-testid="home-admin" maxW="1000px">
      <Flex justify="space-between" align="flex-start" gap="3" flexWrap="wrap">
        <Greeting name={profile?.full_name} sub={`Here's how ${companyName || "your company"} is doing.`} />
        <ScheduleFormButton />
      </Flex>

      <Box mb="6"><AssignedTasks compact /></Box>

      <NeedsAttention />

      <SimpleGrid columns={{ base: 2, lg: 4 }} gap="4" mb="6">
        <StatTile icon={CalendarDays} label="This week" value={week.length} sub="submissions in 7 days"
          accent={ACCENT_TEAL} unavailable={unknown} to="/submissions" testId="home-stat-week" />
        <StatTile icon={ClipboardCheck} label="Needs review" value={urgentCount ?? pending.filter(isUrgent).length} sub="flagged or review-required"
          accent={ACCENT_AMBER} unavailable={unknown} to="/submissions?status=pending" testId="home-stat-pending" />
        <StatTile icon={CheckCircle2} label="Resolved" value={resolved.length} sub="signed off"
          accent={ACCENT_RESOLVED} unavailable={unknown} to="/submissions?status=resolved" testId="home-stat-resolved" />
        <StatTile icon={Siren} label="Open incidents" value={openIncidents.length} sub="incident reports pending"
          accent={ACCENT_POPPY} unavailable={unknown} to="/submissions?type=Incident%20Report&status=pending" testId="home-stat-incidents" />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 3 }} gap="4">
        <Box gridColumn={{ base: "auto", lg: "span 2" }}>
          <HomeCard title="Recent submissions" testId="home-recent"
            action={<Link as={RouterLink} to="/submissions" fontSize="xs" color={colors.tealHover} fontWeight="600" data-testid="home-view-all-subs">View all</Link>}>
            {recent.length === 0 ? (
              <EmptyState>{unknown ? "You're offline — recent submissions will appear once this device has loaded them." : "No submissions yet — publish a form template and your field team's work will appear here."}</EmptyState>
            ) : (
              <RecentSubs subs={recent} onOpen={(id) => navigate(`/submissions/${id}`)} />
            )}
          </HomeCard>
        </Box>
        <Stack gap="3">
          <SafetyBulletinsCard />
          <Text fontWeight="700" fontSize="sm" color={colors.textPrimary}>Manage</Text>
          <QuickLink icon={Users} label="Employees & Locations" desc="Invite people, assign job sites" to="/setup" testId="home-link-setup" />
          <QuickLink icon={ClipboardList} label="Form Templates" desc="Build & publish field forms" to="/templates" testId="home-link-templates" />
          {hasFeature("analytics") && <QuickLink icon={BarChart3} label="Analytics" desc="Trends, incidents & compliance" to="/analytics" testId="home-link-analytics" />}
          {hasFeature("safety_bulletins") && <QuickLink icon={BookOpen} label="Resources" desc="Company document library" to="/resources" testId="home-link-resources" />}
        </Stack>
      </SimpleGrid>
    </Box>
  );
}
